import React, { useState } from 'react';
import GlobalStyle from './styles/global';
import { HashRouter, Route, Routes } from 'react-router-dom';
import ProtectedRoute from './components/helpers/ProtectedRoute';
import { Fonts } from './constants/fonts';
import { createTheme, ThemeProvider } from '@material-ui/core/styles';
import { Colors } from './constants/colors';
import { RegistrationDone } from './components/registration/RegistrationDone';
import { Navigate } from 'react-router';
import { ReceiptOrder } from './components/ReceiptOrder';
import { useDispatch, useSelector } from 'react-redux';
import { selectRequestType, selectUser } from './store/auth/auth.selector';
import { RequestType } from './model/enums/RequestType';
import { Configuration, EnrollmentApi, FormsApi, ServiceApi, SubmissionApi, UploadApi } from './generated';
import { getApiUrl } from './hooks/getApiUrl';
import { Registration } from './components/registration/Registartion';
import { Login } from './components/login/Login';
import { Verification } from './components/login/Verification';
import { refreshUser } from './store/auth/actions/refreshUser';
import { Loading } from './components/Loading';
import { useMountEffect } from './hooks/useMountEffect';
import { Breakpoint } from './constants/device';
import { ReceiptOrderDone } from './components/ReceiptOrderDone';
import { BrowserNotSupportedWarning } from './components/BrowserNotSupportedWarning';
import { isBrowserSupported } from './hooks/isBrowserSupported';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { getCaptchaKey } from './hooks/getCaptchaKey';


const apiConfiguration = new Configuration(
  {
    baseOptions: { withCredentials: true }
  }
);

export const api = {
  enrollment: new EnrollmentApi(apiConfiguration, getApiUrl()),
  forms: new FormsApi(apiConfiguration, getApiUrl()),
  service: new ServiceApi(apiConfiguration, getApiUrl()),
  submission: new SubmissionApi(apiConfiguration, getApiUrl()),
  upload: new UploadApi(apiConfiguration, getApiUrl())
};


const theme = createTheme({
  typography: {
    fontFamily: Fonts.base
  },
  palette: {
    primary: {
      light: Colors.primaryLighter,
      main: Colors.primary,
      dark: Colors.primaryDarker,
      contrastText: Colors.white
    }
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: Breakpoint.mobile,
      md: Breakpoint.tablet,
      lg: Breakpoint.laptop,
      xl: Breakpoint.desktop
    }
  }
});

export const App: React.FC = () => {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const requestType = useSelector(selectRequestType);
  const [loading, setLoading] = useState<boolean>(true);

  useMountEffect(async () => {

    // Try to fetch logged in user
    try {
      await dispatch(refreshUser());
      console.log('User is logged in.');
    } catch (e) {
      console.log('User is not logged in.');
    } finally {
      setLoading(false);
    }

    // Refresh session cookie each minute
    setInterval(() => {
      if (user) {
        dispatch(refreshUser());
      }
    }, 60000);
  });

  const router = (
    <HashRouter>
      <Routes>
        <Route path='/login' element={<Login />} />

        <Route path='/verification' element={<ProtectedRoute canActivate={requestType !== undefined} />}>
          <Route path='/verification' element={<Verification />} />
        </Route>

        <Route path='/receipt' element={<ProtectedRoute canActivate={user?.requestType === RequestType.ReceiptOrder} />}>
          <Route path='/receipt' element={<ReceiptOrder />} />
        </Route>

        <Route path='/registration' element={<ProtectedRoute canActivate={RequestType.isRegistration(user?.requestType)} />}>
          <Route path='/registration' element={<Registration />} />
        </Route>

        <Route path='/send-registration' element={<RegistrationDone />} />
        <Route path='/send-receipt-order' element={<ReceiptOrderDone />} />
        <Route path='/' element={<Navigate to='/registration' />} />
      </Routes>
    </HashRouter>
  );

  return (
    <GoogleReCaptchaProvider reCaptchaKey={getCaptchaKey()}>
      <ThemeProvider theme={theme}>
        <GlobalStyle />
        {isBrowserSupported() ? (loading ? <Loading show={loading} /> : router) : (<BrowserNotSupportedWarning />)}
      </ThemeProvider>
    </GoogleReCaptchaProvider>
  );
};

export default App;
